'use client';

import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { cx } from '../utils/cx';
import { getFilesOrigin, resolveAssetUrl, sanitizeUrl } from '../utils/asset-url';
import { useKeyedAnimation } from '../utils/use-keyed-animation';

export type LogoSize = 'sm' | 'md' | 'lg' | 'xl' | number | string;
export type LogoVariant = 'full' | 'mark' | 'rounded' | 'rounded-lg';
export type LogoDecoration = 'none' | 'hero';

export interface LogoProps {
  size?: LogoSize;
  variant?: LogoVariant;
  decoration?: LogoDecoration;
  className?: string;
  animateKey?: string | number;
  animateClassName?: string;
  animateDurationMs?: number;
  animateOnMount?: boolean;
  src?: string;
  srcDark?: string;
  alt?: string;
}

const SIZE_MAP: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: '28px',
  md: '40px',
  lg: '48px',
  xl: '64px',
};

const DEFAULT_SOURCES: Record<LogoVariant, { light: string; dark: string }> = {
  full: { light: '/images/logo-full.svg', dark: '/images/logo-full-dark.svg' },
  mark: { light: '/images/logo-mark.svg', dark: '/images/logo-mark-dark.svg' },
  rounded: { light: '/images/logo-mark.svg', dark: '/images/logo-mark-dark.svg' },
  'rounded-lg': { light: '/images/logo-mark.svg', dark: '/images/logo-mark-dark.svg' },
};

function resolveSizeToCssLength(size: LogoSize): string {
  if (typeof size === 'number') return `${size}px`;
  const s = String(size ?? '').trim();
  if (!s) return SIZE_MAP.md;
  if (s === 'sm' || s === 'md' || s === 'lg' || s === 'xl') return SIZE_MAP[s];
  return s;
}

function decorationClass(decoration: LogoDecoration | undefined): string {
  switch (decoration) {
    case 'hero':
      return 'drop-shadow-[0_10px_32px_rgba(15,23,42,0.16)] dark:drop-shadow-[0_10px_32px_rgba(0,0,0,0.35)]';
    case 'none':
    default:
      return '';
  }
}

function variantShapeClass(variant: LogoVariant): string {
  switch (variant) {
    case 'rounded':
      return 'overflow-hidden rounded-full';
    case 'rounded-lg':
      return 'overflow-hidden rounded-lg';
    case 'full':
    case 'mark':
    default:
      return '';
  }
}

function variantWrapperClass(variant: LogoVariant): string {
  switch (variant) {
    case 'full':
      return 'inline-flex items-center';
    case 'mark':
    case 'rounded':
    case 'rounded-lg':
    default:
      return 'inline-flex items-center justify-center';
  }
}

function InlineLogoMark({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <svg
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ borderRadius: 'inherit', ...style }}
      className={cx('shrink-0', className)}
      aria-hidden="true"
      role="presentation"
    >
      <rect width="40" height="40" rx="12" className="fill-brand dark:fill-brand/80" />
      <path
        d="M 10 28 L 10 12 M 20 28 L 20 12 M 30 28 L 30 12 M 12 20 L 18 12 M 28 20 L 22 12"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function useResolvedColorScheme(): 'light' | 'dark' {
  const [scheme, setScheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const root = document.documentElement;

    const readScheme = () => {
      const cls = root.classList;
      if (cls.contains('dark') || cls.contains('dark-mode')) return 'dark';
      if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) return 'dark';
      return 'light';
    };

    setScheme(readScheme());

    const mql = window.matchMedia?.('(prefers-color-scheme: dark)');
    const onMqlChange = () => setScheme(readScheme());

    if (mql) {
      if ('addEventListener' in mql) {
        mql.addEventListener('change', onMqlChange);
      } else {
        const legacyMql = mql as MediaQueryList & {
          addListener(listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void): void;
          removeListener(listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void): void;
        };
        legacyMql.addListener(onMqlChange);
      }
    }

    const observer = new MutationObserver(() => setScheme(readScheme()));
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();

      if (!mql) return;
      if ('removeEventListener' in mql) {
        mql.removeEventListener('change', onMqlChange);
        return;
      }

      const legacyMql = mql as MediaQueryList & {
        removeListener(listener: (this: MediaQueryList, ev: MediaQueryListEvent) => void): void;
      };
      legacyMql.removeListener(onMqlChange);
    };
  }, []);

  return scheme;
}

export function Logo({
  size = 'md',
  variant = 'full',
  decoration = 'none',
  className,
  animateKey,
  animateClassName,
  animateDurationMs = 350,
  animateOnMount = false,
  src,
  srcDark,
  alt,
}: LogoProps) {
  const filesOrigin = useMemo(() => getFilesOrigin(), []);
  const height = useMemo(() => resolveSizeToCssLength(size), [size]);

  const scheme = useResolvedColorScheme();

  const shouldAnimate = useKeyedAnimation({
    keyValue: animateKey,
    enabled: Boolean(animateClassName && animateClassName.trim()),
    durationMs: animateDurationMs,
    animateOnMount,
  });

  const keyedAnimCls = shouldAnimate ? (animateClassName ?? '') : '';

  const sources = useMemo(() => {
    const defaults = DEFAULT_SOURCES[variant];

    const lightRaw = src ?? defaults.light;
    const darkRaw = srcDark ?? src ?? defaults.dark;

    const lightResolved = sanitizeUrl(resolveAssetUrl(lightRaw, filesOrigin));
    const darkResolved = sanitizeUrl(resolveAssetUrl(darkRaw, filesOrigin));

    return { light: lightResolved, dark: darkResolved };
  }, [filesOrigin, src, srcDark, variant]);

  const [hasError, setHasError] = useState(false);

  const chosenSrc = useMemo(() => {
    const primary = scheme === 'dark' ? sources.dark : sources.light;
    const fallback = scheme === 'dark' ? sources.light : sources.dark;
    return primary || fallback || '';
  }, [scheme, sources.dark, sources.light]);

  const hasAlt = Boolean((alt ?? '').trim());
  const wrapperA11yProps = hasAlt
    ? { role: 'img' as const, 'aria-label': alt }
    : { role: 'presentation' as const, 'aria-hidden': true as const };

  const isSquareVariant =
    variant === 'mark' || variant === 'rounded' || variant === 'rounded-lg';

  const wrapperStyle: CSSProperties = isSquareVariant
    ? variant === 'rounded'
      ? { width: height, height, borderRadius: '9999px' }
      : { width: height, height }
    : { height, width: 'auto' };

  const imgStyle: CSSProperties = isSquareVariant
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        borderRadius: 'inherit',
      }
    : { height: '100%', width: 'auto', maxWidth: '100%', borderRadius: 'inherit' };

  const decorationCls = decorationClass(decoration);
  const shapeCls = variantShapeClass(variant);
  const wrapperCls = variantWrapperClass(variant);

  const shouldShowFallback = !chosenSrc || hasError;

  if (shouldShowFallback) {
    if (variant === 'mark' || variant === 'rounded' || variant === 'rounded-lg') {
      return (
        <span
          style={wrapperStyle}
          className={cx(wrapperCls, shapeCls, decorationCls, keyedAnimCls, className)}
          {...wrapperA11yProps}
        >
          <InlineLogoMark style={{ width: '100%', height: '100%' }} />
        </span>
      );
    }

    const fallbackText = (alt ?? '').trim() || 'Mneno';

    return (
      <span
        style={wrapperStyle}
        className={cx('inline-flex items-center gap-2', decorationCls, keyedAnimCls, className)}
        {...wrapperA11yProps}
      >
        <InlineLogoMark style={{ width: `calc(${height} * 0.82)`, height: `calc(${height} * 0.82)` }} />
        <span className="text-sm font-semibold leading-none tracking-[-0.02em] text-primary">
          {fallbackText}
        </span>
      </span>
    );
  }

  return (
    <span
      style={wrapperStyle}
      className={cx(wrapperCls, shapeCls, decorationCls, keyedAnimCls, className)}
      {...wrapperA11yProps}
    >
      <img
        src={chosenSrc}
        alt=""
        aria-hidden="true"
        draggable={false}
        decoding="async"
        loading="eager"
        style={imgStyle}
        className="block select-none"
        onError={() => setHasError(true)}
      />
    </span>
  );
}

Logo.displayName = 'Logo';